import { ChevronRight, PanelsTopLeft, Clock3 } from "lucide-react";
import type { Player } from "@/lib/types";
import { useCollection } from "@/features/collections/useCollection";
import {
  CollectionPagination,
  CollectionToolbar,
} from "@/features/collections/CollectionControls";

export type EvidenceRow<T> = {
  value: T;
  id: number;
  title: string;
  position: string;
  metadata: string;
  visibility: string;
  duration?: string;
  playerIds: string[];
  active?: boolean;
};
/** A bounded recent collection or a complete match collection, supplied after authorization. */
export function EvidenceList<T>({
  label,
  rows,
  players = [],
  loading,
  empty,
  testId,
  rowTestId,
  onOpen,
}: {
  label: string;
  rows: EvidenceRow<T>[];
  players?: Player[];
  loading?: boolean;
  empty: string;
  testId: string;
  rowTestId: string;
  onOpen: (value: T) => void;
}) {
  const names = (ids: string[]) =>
    ids
      .map((id) => players.find((p) => p.id === id)?.display_name)
      .filter(Boolean)
      .join(", ");
  const collection = useCollection(rows, {
    scope: testId,
    text: (r) => `${r.title} ${r.metadata} ${names(r.playerIds)}`,
    matches: (r, filter) => r.visibility === filter,
  });
  return (
    <section data-testid={testId} className="review-evidence-list">
      <div className="review-evidence-heading">
        <h4>{label}</h4>
        <span>{rows.length} saved</span>
      </div>
      <CollectionToolbar
        collection={collection}
        label={label.toLowerCase()}
        filters={[
          { value: "", label: "All visibility" },
          { value: "private", label: "Coaches only" },
          { value: "team", label: "Whole team" },
          { value: "player", label: "Player + family" },
        ]}
      />
      {loading ? (
        <p role="status">Loading {label.toLowerCase()}…</p>
      ) : !rows.length ? (
        <p className="text-ink-muted">{empty}</p>
      ) : !collection.items.length ? (
        <p>No matching feedback. Clear filters to see all.</p>
      ) : (
        <div className="review-evidence-rows">
          {collection.items.map((row) => (
            <button
              key={row.id}
              type="button"
              className="review-evidence-row"
              data-testid={rowTestId}
              data-coach-note-id={
                rowTestId.includes("clip") ? undefined : row.id
              }
              data-coach-clip-id={
                rowTestId.includes("clip") ? row.id : undefined
              }
              aria-pressed={Boolean(row.active)}
              onClick={() => onOpen(row.value)}
            >
              <span className="review-evidence-position">
                {row.position === "Board" ? (
                  <PanelsTopLeft size={16} aria-hidden />
                ) : (
                  <Clock3 size={16} aria-hidden />
                )}
                {row.position}
              </span>
              <span className="review-evidence-content">
                <strong>{row.title}</strong>
                <span>
                  {row.metadata}
                  {row.duration ? (
                    <>
                      {" "}
                      ·{" "}
                      <span data-testid="coach-review-rail-clip-duration">
                        {row.duration}
                      </span>
                    </>
                  ) : null}
                </span>
              </span>
              <span className="review-evidence-players">
                {names(row.playerIds) ||
                  (row.playerIds.length
                    ? `${row.playerIds.length} players`
                    : "Team")}
              </span>
              <ChevronRight size={18} aria-hidden />
            </button>
          ))}
        </div>
      )}
      <CollectionPagination collection={collection} />
    </section>
  );
}
export function feedbackMeta(
  kind: string,
  slot: string | null,
  category: string,
  visibility: string,
) {
  const labels: Record<string, string> = {
    first_half: "First half",
    second_half: "Second half",
    full: "Full match",
    extra_time: "Extra time",
    penalty_shootout: "Shootout",
    team: "Whole team",
    player: "Player + family",
    players: "Selected players",
    private: "Coaches only",
  };
  return [
    kind,
    slot ? (labels[slot] ?? slot.replaceAll("_", " ")) : null,
    category.replaceAll("_", " "),
    labels[visibility] ?? visibility.replaceAll("_", " "),
  ]
    .filter(Boolean)
    .join(" · ");
}
